import { AppointmentsRepository } from "@/repositories/appointments.repository";
import { appointment_status } from "@/models/appointment.model";
// Individual assignment
import { IndividualsRepository } from "@/repositories/individuals.repository";
import { CreateAppointmentPayload } from "@/app/pmc/appointments/appointments.entity.ts";
import { store } from "@/lib/store";
import { api } from "@/lib/api";
import { everyUnit, toUnitOption, toUnitRequest, type UnitForm, type UnitOption } from "@/repositories/unit-records";
import { bookAppointment } from "@/services/appointment-booking.service";
import { uuid } from "@/interfaces";
import { VendorPropertyServicePricingRepository } from "@/repositories/vendor-property-service-pricing.repository";
import { PropertyRepository } from "@/repositories/properties.repository";
import { listedMoneyOf, type ListedMoney } from "@/lib/appointment-adjustment";

export async function listVendorAppointments(
  page: number,
  pageSize: number,
  filters: {
    status?: appointment_status;
    serviceParent?: string;
    assignee?: string;
    search?: string;
    dateFrom?: string;
    dateTo?: string;
  },
  sortKey: string,
  sortOrder: "asc" | "desc"
) {
  return AppointmentsRepository.listForVendor(page, pageSize, filters, sortKey, sortOrder);
}

export const listVendorAppointmentFilterOptions = async () => {
  return AppointmentsRepository.listFilterOptionsForVendor();
};

export const fetchVendorAppointment = (id: string) => AppointmentsRepository.getById(id);
export const fetchVendorAppointmentLogs = (id: string) => AppointmentsRepository.listHistory(id);

export const listActiveIndividuals = async () => {
  const individuals = await IndividualsRepository.listForAssignment();
  return individuals.map(({ id, name }) => ({ id, name }));
};

export const listActiveSubs = async () => {
  const subs = await IndividualsRepository.listSubsForAssignment();
  return subs.map((sub) => ({
    id: sub.id,
    name: sub.name,
  }));
};

export async function fetchListedMoney(vendorId: uuid, propertyId: uuid, serviceId: uuid): Promise<ListedMoney> {
  return listedMoneyOf(await VendorPropertyServicePricingRepository.listedAt(propertyId, vendorId, serviceId));
}

// New method to get assignable resources
export const fetchAssignableResources = async () => {
  return IndividualsRepository.listAssignableResources();
};

// Booking-related API functions
export const fetchVendorParentCategories = async (): Promise<string[]> => {
  const vendorOrgId = store.getState().organization.activeOrgId;
  return AppointmentsRepository.listParentCategoriesForVendor(vendorOrgId);
};

export const fetchVendorChildServices = async (parentName: string): Promise<{ id: string; name: string }[]> => {
  const vendorOrgId = store.getState().organization.activeOrgId;
  return AppointmentsRepository.listChildServices(vendorOrgId, parentName);
};

export const fetchVendorProperties = async (): Promise<{ id: string; name: string }[]> => {
  const vendorOrgId = store.getState().organization.activeOrgId;
  const properties = await PropertyRepository.servicedBy(vendorOrgId);
  return properties.map(({ id, name }) => ({ id, name }));
};

export const fetchUnitsForProperty = async (propertyId: uuid): Promise<UnitOption[]> => {
  const vendorOrgId = store.getState().organization.activeOrgId;
  if (!vendorOrgId) return [];
  return (await everyUnit(api.units.list, propertyId, { actingOrganization: vendorOrgId })).map(toUnitOption);
};

export const createVendorUnit = async (unit: UnitForm & { property: uuid }): Promise<UnitOption> =>
  toUnitOption(await api.units.create(unit.property, toUnitRequest(unit)));

export const createVendorAppointment = (payload: Omit<CreateAppointmentPayload, "vendor">) => bookAppointment(payload);
